import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ProductOutput,
  useDeleteDraftRecommendationMutation,
  useGetShopProductsQuery,
  useRecommendProductsMutation,
} from 'graphql/generated/remote-schema-hasura';
import { ProductCategory } from '../../../../shop';

export interface Product {
  description: string;
  id: number;
  short_description: string;
  title: string;
  price: number;
}

export const productDataMock = {
  products: [
    {
      id: 1,
      title: 'Hormone Test',
      short_description:
        'Hormonal imbalances lead to issues including adult male hormonal acne and female menopausal dryness. This test measures five key saliva hormones. Your DECODE provider can provide insight into these imbalances and how to resolve them.',
      description:
        'The MySkin Hormone test will provide key measurements of estrogen, progesterone (and their ratio), DHEA-S, testosterone, and diurnal cortisol (daily cycle). Hormones are signaling molecules that are transported through the body and regulate your body and behavior. Hormone dysregulation can happen at any point throughout life. When our hormone levels are not in balance, we often feel the effects, such as mood dysregulation, disrupted sleep, and weight or body temperature changes. Hormones affect skin health through oil and collagen production, as well as hydration. When hormone levels fluctuate, they can lead to skin issues like acne, dry skin, and wrinkles. Estrogen - Helps produce collagen, elastin, and hyaluronic acid, which keep skin firm and plump. Estrogen levels decrease with age, which can lead to thinner, drier, less elastic skin, and wrinkles. During menopause, estrogen levels can drop suddenly, which can cause dry or itchy skin, acne, and sagging. Progesterone - Stimulates the production of sebum, the skins natural oil. Progesterone can also cause skin to swell, which can make pores look smaller. However, too much progesterone can lead to oil buildup and acne. Testosterone/DHEA-S - Stimulates the sebaceous glands to produce sebum, which can lead to oily skin and acne. Testosterone levels are typically higher during menstruation, and hormonal acne can appear in the T-zone (forehead, nose, and chin) during puberty. Cortisol - Produced by the adrenal glands in response to stress, high cortisol levels can lead to inflammation, which can make skin conditions like acne and eczema worse.',
      price: 225,
      specs: {
        contents: 'Test tube with cheek swab',
        expiration_period: 1,
        made_in: 'USA',
        __typename: 'Specs',
      },
      __typename: 'ProductOutput',
    },
    {
      id: 2,
      title: 'Inflammation (hs-CRP) Blood Spot Test',
      short_description:
        'C-reactive protein (CRP) levels increase when there is inflammation. This test measures your hs-CRP levels using blood spots (fingerpick). Your DECODE provider can provide insight into potential root causes and how to resolve them.',
      description:
        'The MyHealth Inflammation high-sensitivity C-reactive protein (hs-CRP) test measures the level of C-reactive protein (CRP) in your blood, which is a marker of inflammation. CRP levels increase when there is inflammation or infection in the body, which could be due to a serious injury, chronic infection, systemic disease (such as cardiovascular diseases), auto-immune issues, and skin conditions such as psoriasis, rosacea or severe acne and eczema.',
      price: 130,
      specs: {
        contents: 'Blood spot collection card, 2 lancets',
        expiration_period: 1,
        made_in: 'USA',
        __typename: 'Specs',
      },
      __typename: 'ProductOutput',
    },
    {
      id: 3,
      title: 'Toxic & Essential Elements - Blood Spot and Urine Test',
      short_description:
        'Heavy metals exposure from food or environment can impact your skin and health. This test measures your heavy metals levels using blood spots and urine strips. Your DECODE provider can account for exposure and advise on risk and treatment.',
      description:
        'The MyHealth Toxic & Essential Elements test measures your exposure to heavy metals and levels of essential elements in two ways: Blood Profile – mercury (Hg), cadmium (Cd), zinc (Zn), copper (Cu), selenium (Se) and magnesium (Mg) Urine Profile – iodine (I), bromine (Br), lithium (Li), arsenic (As), selenium (Se), Cadmium (Cd), mercury (Hg) and creatinine (Crtn) Why both blood and urine? We obtain more comprehensive information. For example, urinary cadmium is the best measure of accumulated exposure, while blood spot cadmium assesses only recent exposure. Heavy metals are commonly found in our food and environment, so we are exposed to them daily. The amounts that we typically inhale, ingest, or touch are usually harmless; however, heavy metals can cause skin issues, including allergic reactions, chronic poisoning, and dermatitis: Allergic reactions When the body encounters a metal its allergic to, skin cells pick up metal molecules that travel to the lymph nodes. The body then treats the metal as a foreign invader and mounts an immune response, which can cause symptoms like redness, itching, swelling, or a rash. These reactions can range from mild to severe. Metals like nickel, chromium, cobalt, and copper are common triggers for eczema and contact dermatitis. Chronic poisoning Chronic poisoning from heavy metals like lead, mercury, arsenic, and cadmium can cause skin and nail symptoms like hardened skin patches, unusual skin darkening, and white bands on fingernails. Symptoms of chronic poisoning include weakness, muscle aches, chills, and fever. Dermatitis Heavy metals like chromium, cobalt, and nickel can be found in cases of dermatitis and eczema, which can cause symptoms like redness, itching, dryness, and starch.',
      price: 280,
      specs: {
        contents: '2 urine strips, 1 blood spot collection card, 2 lancets',
        expiration_period: 1,
        made_in: 'USA',
        __typename: 'Specs',
      },
      __typename: 'ProductOutput',
    },
    {
      id: 4,
      title: 'Neurotransmitter Urine Test',
      short_description:
        'Neurotransmitters can influence the development and exacerbation of skin conditions and general health. This test measures your neurotransmitter levels using urine strips. Your DECODE provider will explain your data and suggest treatment options.',
      description:
        'The MyBrain Neurotransmitter test measures your diurnal levels and potential neurochemical imbalance to optimize mood/stress, sleep, or adrenal function, and to help your provider detemine how they affect your skin health through the skin-gut-brain axis. Neurotransmitters and other neurochemicals may play a role in the development and exacerbation of skin conditions: Stress, anxiety, and depression can trigger or worsen many inflammatory skin conditions, including acne, psoriasis, atopic dermatitis, contact dermatitis, alopecia areata, itch, and erythema. The COVID-19 pandemic provided evidence of this connection, with dermatologists reporting an increase in skin flares during that time. Some neurotransmitters implicated in mental illness, such as serotonin and gamma-aminobutyric acid (GABA), may also play a role in skin conditions like psoriasis and atopic dermatitis. Other neurotransmitters that may be involved in psoriasis include dopamine and norepinephrine. Immune-mediating neuropeptides may help treat inflammatory skin diseases like psoriasis. Neuropeptides can activate mast cells, which are immune cells that respond to stress and are often located near sensory nerves and blood vessels in the skin.',
      price: 320,
      specs: {
        contents: '4 urine strips',
        expiration_period: 1,
        made_in: 'USA',
        __typename: 'Specs',
      },
      __typename: 'ProductOutput',
    },
  ],
};

export interface UseRecommendedProductsOutput {
  selectedProducts: Record<string, number>;
  updateSelectedProducts: Dispatch<SetStateAction<Record<string, number>>>;
  handleDelete: (id: number) => void;
  handleSaveProduct: () => void;
  handleCancelSaveProduct: () => void;
  saveProducts: { id: number; title: string; amount: number }[];
  products: ProductOutput[];
  handleRecommendProduct: (codexPatientId: string) => Promise<void>;
  removeDraftRecommendation: (recommendedId: string) => Promise<unknown>;
}

export const RECOMMENDED_PRODUCT = 'recommended-product';
export const VERADIGM = 'veradigm';

export const useRecommendedProducts = (
  defaultProduct?: Record<string, number>,
): UseRecommendedProductsOutput => {
  const { data } = useGetShopProductsQuery({
    fetchPolicy: 'cache-first',
    variables: { limit: 10, offset: 0, categories: ProductCategory.TestKit },
  });

  const savedProducts = useMemo(() => defaultProduct ?? {}, [defaultProduct]);

  const [recommendProducts] = useRecommendProductsMutation();
  const [removeRecommendation] = useDeleteDraftRecommendationMutation();

  const removeDraftRecommendation = (recommendationId: string) =>
    removeRecommendation({
      variables: { recommendationId },
    });

  const [selectedProducts, updateSelectedProducts] = useState<
    Record<string, number>
  >(() => ({ ...savedProducts }));
  const [saveProducts, updateSaveProducts] = useState<Record<string, number>>(
    () => ({ ...savedProducts }),
  );

  useEffect(() => {
    updateSelectedProducts(savedProducts);
    updateSaveProducts(savedProducts);
  }, [savedProducts]);

  const products = useMemo(() => data?.getProducts.products || [], [data]);

  const handleSaveProduct = useCallback(
    () => updateSaveProducts(selectedProducts),
    [selectedProducts],
  );

  const handleRecommendProduct = useCallback(
    async (codexPatientId: string) => {
      await recommendProducts({
        variables: {
          codexPatientId,
          recommendations: products
            .map(({ id }) => ({
              productCodexId: id,
              quantity: selectedProducts[id],
            }))
            .filter(({ quantity }) => !!quantity),
        },
      });
    },
    [products, recommendProducts, selectedProducts],
  );

  const handleCancelSaveProduct = useCallback(
    () => updateSelectedProducts(saveProducts),
    [saveProducts],
  );

  const handleDelete = useCallback(
    (id: number) => {
      updateSaveProducts((prevState) => ({
        ...prevState,
        [id]: 0,
      }));
      updateSelectedProducts((prevState) => ({
        ...prevState,
        [id]: 0,
      }));
    },
    [updateSaveProducts, updateSelectedProducts],
  );

  const saveProductCard = useMemo(
    () =>
      products
        .map(({ id, title }) => ({
          id,
          title,
          amount: saveProducts[id],
        }))
        .filter(({ amount }) => !!amount),
    [products, saveProducts],
  );

  return {
    selectedProducts,
    updateSelectedProducts,
    handleDelete,
    handleSaveProduct,
    handleCancelSaveProduct,
    saveProducts: saveProductCard,
    products: products as ProductOutput[],
    removeDraftRecommendation,
    handleRecommendProduct,
  };
};
